import { IUserNotification, IUserNotificationSnapshot } from 'src/models';
import {
  IDrugInfo,
  IUserMedicine,
  IUserNotificationMedicine,
} from 'src/models/user-notification/UserNotificationMedicine';

import { TApiResponseData } from '../apiTypes';
import instance from '../axios';

// ===========================================================================
// 알림 유형 설정 (swagger /v1/user/ntfctn/cfg)
//   - GET  : 사용자가 "켠" 알림 유형 코드 목록 (목록에 있으면 ON)
//   - PUT  : 단건 토글 { ntfctnTypeCd, useYn } 저장 → 저장된 단건 반환
// 공통코드 객체 { pcode, code, value } 형태. notiTime/약물 전용 필드는 스펙에 없음.
// ===========================================================================

/** 알림 유형 공통코드 ({ pcode, code, value }) */
export interface INotificationTypeCode {
  pcode: number;
  code: number;
  value: string;
}

/** PUT /v1/user/ntfctn/cfg 단건 저장 결과 */
export interface INotificationConfigItem {
  ntfctnTypeCd: INotificationTypeCode;
  useYn: boolean;
}

const NTFCTN_CFG_URL = '/v1/user/ntfctn/cfg';

/** 알림 유형 설정 목록 조회 — 켜진 유형 코드 배열 반환 */
export const getNotificationConfig = async () => {
  try {
    const response = await instance.get<TApiResponseData<INotificationTypeCode[]>>(NTFCTN_CFG_URL);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 알림 유형 단건 토글 저장 (PUT) */
export const setNotificationConfig = async (
  ntfctnTypeCd: INotificationTypeCode,
  useYn: boolean,
) => {
  try {
    const response = await instance.put<TApiResponseData<INotificationConfigItem>>(NTFCTN_CFG_URL, {
      ntfctnTypeCd,
      useYn,
    });
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/**
 * @deprecated 레거시 nicocare 엔드포인트(/userNotification). swagger 스펙은
 * getNotificationConfig/setNotificationConfig(/v1/user/ntfctn/cfg)를 사용.
 */
export const getUserNotification = async () => {
  try {
    const url = `/userNotification`;

    const response = await instance.get<TApiResponseData<IUserNotification[]>>(url);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/**
 * @deprecated 레거시 nicocare 엔드포인트(/userNotification). swagger 스펙은
 * setNotificationConfig(/v1/user/ntfctn/cfg)를 사용.
 */
export const setUserNotification = async (notification: IUserNotification) => {
  try {
    const url = `/userNotification`;

    const response = await instance.put<TApiResponseData<IUserNotification[]>>(url, notification);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 알림 생성 (새 알림 추가용) */
export const createUserNotification = async (notification: IUserNotificationSnapshot) => {
  try {
    const url = `/userNotification`;

    const response = await instance.put<TApiResponseData<IUserNotification[]>>(url, notification);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 복용약 알림 조회 */
export const getUserNotificationMedicine = async () => {
  try {
    const url = `/userNotification/medicine`;

    const response = await instance.get<TApiResponseData<IUserNotificationMedicine>>(url);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 복용약 알림 설정 (복용약 없는 경우) */
export const setDefaultUserNotificationMedicine = async (userMedicine: IUserMedicine) => {
  try {
    const url = `/userNotification/medicine`;
    const payload = {
      userMedicine,
      userMedicineNotificationList: [
        {
          notiYn: 'N',
          notiTime: null,
        },
        {
          notiYn: 'N',
          notiTime: null,
        },
      ],
    };

    const response = await instance.post<TApiResponseData<IUserNotificationMedicine>>(url, payload);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 복용약 알림 설정 및 수정 */
export const setUserNotificationMedicine = async (userNotiMedicine: IUserNotificationMedicine) => {
  try {
    const url = `/userNotification/medicine`;

    const response = await instance.put<TApiResponseData<IUserNotificationMedicine>>(
      url,
      userNotiMedicine,
    );
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 복용약 목록 조회 */
export const getDrugInfo = async () => {
  try {
    const url = `/drugInfo`;

    const response = await instance.get<TApiResponseData<IDrugInfo[]>>(url);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/**
 * 디바이스 토큰 등록/갱신 (POST /v1/user/device, FCM)
 * swagger 요청: { deviceToken, osTypeCd?, ver? } — osTypeCd/ver은 앱에서 받으면 함께 전송.
 */
export const saveDeviceToken = async (
  deviceToken: string,
  options?: { osTypeCd?: INotificationTypeCode; ver?: string },
) => {
  try {
    const url = `/v1/user/device`;
    const payload = {
      deviceToken,
      ...(options?.osTypeCd ? { osTypeCd: options.osTypeCd } : {}),
      ...(options?.ver ? { ver: options.ver } : {}),
    };

    const response = await instance.post<TApiResponseData<null>>(url, payload);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};
